import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Button,
  Card,
  CardHeader,
  Container,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { ThreeDots } from 'react-loader-spinner';
import { Page } from '../../../components';
import Label from '../../../components/label';
import Scrollbar from '../../../components/scrollbar';
import { UserListHead } from '../../../sections/@dashboard/user';
import useUrlFilters from '../../../hooks/useUrlFilters';
import { fToNow } from '../../../utils/formatTime';
import { getInbox } from '../store/workflowActions';
import { RATING_COLORS, STATUS_COLORS, TYPE_LABELS, humanize } from '../components/labels';

// Module-level so useUrlFilters can memoise on its identity. Blank scope is the
// inbox itself (awaiting_me), so the default view needs nothing in the URL.
export const EMPTY_INBOX_FILTERS = { scope: '', bucket: '', type: '', searchTerm: '' };

const SCOPES = [
  { value: '', label: 'Needs my action' },
  { value: 'mine', label: 'Raised by me' },
  { value: 'all', label: 'Everything I can see' },
];
const BUCKETS = [
  { value: '', label: 'All' },
  { value: 'approvals', label: 'Approvals' },
  { value: 'receipts', label: 'Receipts' },
  { value: 'next_steps', label: 'My next steps' },
];
const TYPES = [
  { value: '', label: 'All types' },
  { value: 'transfer', label: 'Transfers' },
  { value: 'disposal', label: 'Disposals' },
  { value: 'repair', label: 'Maintenance' },
  { value: 'regularization', label: 'Regularizations' },
];

const TABLE_HEAD = [
  { id: 'reference', label: 'Request' },
  { id: 'asset', label: 'Asset' },
  { id: 'status', label: 'Status' },
  { id: 'rating', label: 'Rating' },
  { id: 'initiator', label: 'Raised by' },
  { id: 'created', label: 'Raised' },
  { id: '' },
];

/**
 * Everything waiting on the signed-in user -- approvals, receipts and next
 * steps -- across transfers, disposals, maintenance and regularizations, with
 * "raised by me" and "everything" as the other two views.
 */
export default function Inbox() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isLoading, requests, pagination } = useSelector((state) => state.workflowReducer);
  const { filters, page, rowsPerPage, setPage, setRowsPerPage, applyFilters } = useUrlFilters(EMPTY_INBOX_FILTERS);

  const [searchTerm, setSearchTerm] = useState(filters.searchTerm);
  useEffect(() => {
    setSearchTerm(filters.searchTerm);
  }, [filters]);

  useEffect(() => {
    dispatch(getInbox({ page: page + 1, rowsPerPage, ...filters }));
  }, [dispatch, page, rowsPerPage, filters]);

  const runSearch = () => {
    if (searchTerm === filters.searchTerm) return;
    applyFilters({ ...filters, searchTerm });
  };

  const inbox = !filters.scope;

  return (
    <Page title="Inbox">
      <Container maxWidth="xl">
        <Card>
          <CardHeader title="Inbox" subheader="Requests waiting on you, and the requests you raised" />
          <Box sx={{ px: 3, pt: 2 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
              <TextField
                select
                size="small"
                label="Show"
                // '' is a real choice here ("Needs my action", "All types"), which
                // MUI renders blank unless told to.
                SelectProps={{ displayEmpty: true }}
                InputLabelProps={{ shrink: true }}
                value={filters.scope}
                onChange={(event) => applyFilters({ ...filters, scope: event.target.value, bucket: '' })}
                sx={{ minWidth: 200 }}
              >
                {SCOPES.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                label="Type"
                // '' is a real choice here ("Needs my action", "All types"), which
                // MUI renders blank unless told to.
                SelectProps={{ displayEmpty: true }}
                InputLabelProps={{ shrink: true }}
                value={filters.type}
                onChange={(event) => applyFilters({ ...filters, type: event.target.value })}
                sx={{ minWidth: 170 }}
              >
                {TYPES.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
              <Box sx={{ flexGrow: 1 }} />
              <TextField
                size="small"
                sx={{ minWidth: { xs: 180, sm: 300 } }}
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                onBlur={runSearch}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') runSearch();
                }}
                placeholder="Search reference, tag or description..."
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Stack>
          </Box>
          {inbox && (
            <Tabs
              value={filters.bucket}
              onChange={(event, bucket) => applyFilters({ ...filters, bucket })}
              sx={{ px: 3 }}
            >
              {BUCKETS.map((option) => (
                <Tab key={option.value} value={option.value} label={option.label} />
              ))}
            </Tabs>
          )}
          <Scrollbar>
            <TableContainer sx={{ minWidth: 900 }} component={Paper}>
              {isLoading ? (
                <Stack alignItems="center" sx={{ py: 4 }}>
                  <ThreeDots height="80" width="80" radius="9" color="#2065d1" visible />
                </Stack>
              ) : (
                <Table size="small">
                  <UserListHead headLabel={TABLE_HEAD} rowCount={requests?.length} />
                  <TableBody>
                    {requests?.length ? (
                      requests.map((request) => (
                        <TableRow
                          hover
                          key={request.id}
                          sx={{ cursor: 'pointer' }}
                          onClick={() => navigate(`/dashboard/requests/${request.id}`)}
                        >
                          <TableCell>
                            <Typography variant="subtitle2">{request.reference}</Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                              {TYPE_LABELS[request.type]}
                              {request.source === 'WEB' ? '' : ' · from the app'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{request.asset?.description}</Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                              {request.asset?.tag}
                              {request.asset_count > 1 ? ` + ${request.asset_count - 1} more` : ''}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Label color={STATUS_COLORS[request.status] || 'default'}>{humanize(request.status)}</Label>
                          </TableCell>
                          <TableCell>
                            {request.asset?.rating && (
                              <Label color={RATING_COLORS[request.asset.rating.level] || 'default'}>
                                {request.asset.rating.level}
                              </Label>
                            )}
                          </TableCell>
                          <TableCell>{request.initiator_name}</TableCell>
                          <TableCell>{fToNow(request.created_at)}</TableCell>
                          <TableCell align="right">
                            {request.next_action ? (
                              <Button size="small" variant="contained">
                                {request.next_action.label}
                              </Button>
                            ) : (
                              <Button size="small" variant="outlined">
                                View
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={TABLE_HEAD.length}>
                          <Typography variant="body2" sx={{ py: 3, textAlign: 'center', color: 'text.secondary' }}>
                            {inbox ? 'You are all caught up.' : 'No requests match.'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              )}
            </TableContainer>
          </Scrollbar>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            component="div"
            count={pagination?.count || 0}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(event, next) => setPage(next)}
            onRowsPerPageChange={(event) => setRowsPerPage(parseInt(event.target.value, 10))}
          />
        </Card>
      </Container>
    </Page>
  );
}
